import type {
  AdapterAPI,
  SsaiAdBreakMetadata,
  SsaiAdMetadata,
  SsaiAdQuartile,
  SsaiAdQuartileMetadata,
  SsaiApi,
} from '../../api/AdapterAPI';
import {Analytics} from '../../core/Analytics';
import {
  hasValidSsaiAdBreakMetadataStructure,
  hasValidSsaiAdMetadataStructure,
  hasValidSsaiAdQuartileStructure,
  sanitizeSsaiAdQuartileMetadata,
  simplySanitizeSsaiAdBreakMetadata,
  simplySanitizeSsaiAdMetadata,
} from '../../core/ssai/ssaiApiUtils';
import type {AnalyticsConfig} from '../../types/AnalyticsConfig';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {CustomDataValues} from '../../types/CustomDataValues';
import {hasValidSourceMetadataStructure, sanitizeSourceMetadata} from '../../types/SourceMetadata';
import {guardAgainstDuplicatedUserId, sanitizeAnalyticsConfig} from '../../utils/analyticsConfigUtils';
import {areCollectorRequirementsValid} from '../../utils/collectorRequirementChecks';
import {logger} from '../../utils/Logger';
import {
  hasPlayerAlreadyBeenAugmented,
  isDefined,
  markPlayerInstanceAsAugmented,
} from '../../utils/playerAugmentationUtils';
import {VERSION} from '../../utils/Version';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {THEOplayerInternalAdapter} from './THEOplayerInternalAdapter';

export class THEOplayerAdapter implements AdapterAPI {
  private readonly internalAdapter: InternalAdapterAPI | undefined;
  private readonly analytics: Analytics | undefined;

  constructor(config: AnalyticsConfig, player: any, opts?: AnalyticsStateMachineOptions) {
    if (hasPlayerAlreadyBeenAugmented(player.element)) {
      logger.errorMessageToUser('Bitmovin Analytics is already hooked up to this player instance');
      return;
    }
    // Since the THEOplayer instance is a frozen object, we can't directly add properties to it.
    // To work around this, we add the augmentation marker to the player's HTML element.
    markPlayerInstanceAsAugmented(player.element);

    const sanitizedConfig = sanitizeAnalyticsConfig(config);
    if (!areCollectorRequirementsValid(sanitizedConfig)) {
      return;
    }

    guardAgainstDuplicatedUserId(sanitizedConfig);

    this.internalAdapter = new THEOplayerInternalAdapter(player, opts);
    this.analytics = Analytics.create(sanitizedConfig, this.internalAdapter);
  }

  readonly ssai: SsaiApi = {
    adBreakStart: (adBreakMetadata?: SsaiAdBreakMetadata) => {
      if (!isDefined(this.internalAdapter)) return;

      if (!hasValidSsaiAdBreakMetadataStructure(adBreakMetadata)) {
        // if we don't have valid ad break metadata, we don't want to proceed and use SSAI API
        return;
      }

      const sanitizedAdBreakMetadata = simplySanitizeSsaiAdBreakMetadata(adBreakMetadata);

      this.internalAdapter.ssaiService.adBreakStart(sanitizedAdBreakMetadata);
    },

    adStart: (adMetadata?: SsaiAdMetadata) => {
      if (!isDefined(this.analytics)) return;
      if (!isDefined(this.internalAdapter)) return;

      if (!hasValidSsaiAdMetadataStructure(adMetadata)) {
        // if we don't have valid ad metadata, we don't want to proceed and use SSAI API
        return;
      }

      const sanitizedAdMetadata = simplySanitizeSsaiAdMetadata(adMetadata);

      this.internalAdapter.ssaiService.adStart(sanitizedAdMetadata);
      // we need to call this here to revert the customData values to the values before the adBreak
      // this is mainly needed to have clear transitions between ads, and we don't
      // copy over customData values from the first ad into the second ad
      // the adMetadata is applied after this call in the manipulate method when a sample is sent
      // it is a bit of a hack, but it is necessary right now due to the way how samples are created
      this.analytics.setConfigParameters();
      this.analytics.setAutoDetectedDeviceInformation();
    },

    adQuartileFinished: (adQuartile: SsaiAdQuartile, adQuartileMetadata?: SsaiAdQuartileMetadata) => {
      if (!isDefined(this.analytics)) return;
      if (!isDefined(this.internalAdapter)) return;

      if (!hasValidSsaiAdQuartileStructure(adQuartile)) {
        // if we don't have valid ad quartile, we don't want to proceed and use SSAI API
        return;
      }

      const sanitizedSsaiAdQuartileMetadata = sanitizeSsaiAdQuartileMetadata(adQuartileMetadata);

      this.internalAdapter.ssaiService.adQuartileFinished(adQuartile, sanitizedSsaiAdQuartileMetadata);
    },

    adBreakEnd: () => {
      if (!isDefined(this.analytics)) return;
      if (!isDefined(this.internalAdapter)) return;

      this.internalAdapter.ssaiService.adBreakEnd();
      // we need to call this here to revert the customData values to the values we had before the ad
      this.analytics.setConfigParameters();
      this.analytics.setAutoDetectedDeviceInformation();
    },
  };

  static readonly version: string = VERSION;

  get version(): string {
    return VERSION;
  }

  getCurrentImpressionId(): string | undefined {
    if (!isDefined(this.analytics)) return;

    return this.analytics.getCurrentImpressionId();
  }

  getUserId(): string | undefined {
    if (!isDefined(this.analytics)) return;

    return this.analytics.getUserId();
  }

  setCustomData(values: CustomDataValues) {
    if (!isDefined(this.internalAdapter)) return;

    this.internalAdapter.setCustomData(values);
  }

  setCustomDataOnce(values: CustomDataValues) {
    if (!isDefined(this.analytics)) return;

    this.analytics.setCustomDataOnce(values);
  }

  sourceChange(config: AnalyticsConfig) {
    if (!isDefined(this.analytics)) return;

    this.analytics.sourceChange(config);
  }

  programChange(sourceMetadata: {
    /* eslint-disable perfectionist/sort-object-types */
    title?: string;
    videoId?: string;
    cdnProvider?: string;
    path?: string;
    isLive?: boolean;
    customData?: CustomDataValues;
    /* eslint-enable perfectionist/sort-object-types */
  }) {
    if (!hasValidSourceMetadataStructure(sourceMetadata)) {
      // if we don't have valid source metadata, we don't want to proceed
      return;
    }

    const sanitizedSourceMetadata = sanitizeSourceMetadata(sourceMetadata);

    if (!isDefined(this.internalAdapter)) return;

    this.internalAdapter.programChange(sanitizedSourceMetadata);
  }
}
